import { useState, useEffect, useRef } from 'react'
import { Search, X, FolderOpen, Award, BookOpen, Users, ChevronRight, Package, Briefcase } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002/api'

export default function GlobalSearch() {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState({
    projects: [],
    certifications: [],
    competences: [],
    blocs: [],
    metiers: [],
    users: []
  })
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const searchRef = useRef(null)
  const navigate = useNavigate()

  // Fermer le menu si on clique à l'extérieur
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Recherche avec debounce
  useEffect(() => {
    if (query.length < 2) {
      setResults({ projects: [], certifications: [], competences: [], blocs: [], metiers: [], users: [] })
      setIsOpen(false)
      return
    }

    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        const token = localStorage.getItem('token')
        const headers = { Authorization: `Bearer ${token}` }

        // Recherches parallèles avec gestion d'erreur pour éviter le blocage du rate limiter
        const [projectsRes, certificationsRes, competencesRes, blocsRes, metiersRes, usersRes] = await Promise.allSettled([
          axios.get(`${API_URL}/search/projects?q=${encodeURIComponent(query)}`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/search/rncp?q=${encodeURIComponent(query)}`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/search/competences?q=${encodeURIComponent(query)}`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/search/blocs?q=${encodeURIComponent(query)}`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/search/metiers?q=${encodeURIComponent(query)}`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/search/users?q=${encodeURIComponent(query)}`, { headers }).catch(() => ({ data: [] }))
        ])

        setResults({
          projects: projectsRes.status === 'fulfilled' ? projectsRes.value.data : [],
          certifications: certificationsRes.status === 'fulfilled' ? certificationsRes.value.data : [],
          competences: competencesRes.status === 'fulfilled' ? competencesRes.value.data : [],
          blocs: blocsRes.status === 'fulfilled' ? blocsRes.value.data : [],
          metiers: metiersRes.status === 'fulfilled' ? metiersRes.value.data : [],
          users: usersRes.status === 'fulfilled' ? usersRes.value.data : []
        })
        setIsOpen(true)
      } catch (error) {
        console.error('Erreur de recherche:', error)
      } finally {
        setLoading(false)
      }
    }, 1000) // Debounce de 1 seconde - recherche uniquement après avoir fini de taper

    return () => clearTimeout(timer)
  }, [query])

  const handleResultClick = (type, item) => {
    setIsOpen(false)
    setQuery('')

    switch (type) {
      case 'project':
        navigate(`/projects/${item.id}`)
        break
      case 'certification':
        navigate(`/rncp-visualization?focus=${item.rncpCode}`)
        break
      case 'competence':
        navigate(`/competences?focus=${item.id}`)
        break
      case 'bloc':
        navigate(`/competences?focus=bloc-${item.id}`)
        break
      case 'metier':
        navigate(`/competences?focus=metier-${item.codeRome}`)
        break
      case 'user':
        navigate(`/settings?tab=team&user=${item.id}`)
        break
    }
  }

  const totalResults =
    results.projects.length +
    results.certifications.length +
    results.competences.length +
    results.blocs.length +
    results.metiers.length +
    results.users.length

  return (
    <div ref={searchRef} style={{ position: 'relative', width: '100%' }}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder="Rechercher..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          className="w-full pl-10 pr-10 py-2 bg-gray-50/80 backdrop-blur-sm border border-gray-200 rounded-lg text-sm
                   text-gray-900 placeholder-gray-400
                   focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                   transition-all duration-200"
        />
      </div>
      {query && (
        <button
          onClick={() => {
            setQuery('')
            setIsOpen(false)
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded transition-colors"
        >
          <X className="h-4 w-4 text-gray-500" />
        </button>
      )}

      {/* Menu de résultats */}
      {isOpen && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-gray-100 rounded-lg shadow-xl z-20 animate-slide-down p-4" style={{
          height: '400px',
          overflowY: 'auto'
        }}>
          {loading ? (
            <div className="p-4 text-center text-muted">
              Recherche en cours...
            </div>
          ) : totalResults === 0 ? (
            <div className="p-4 text-center text-muted">
              Aucun résultat pour "{query}"
            </div>
          ) : (
            <>
              {/* Projets */}
              {results.projects.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-muted">
                    PROJETS ({results.projects.length})
                  </div>
                  {results.projects.slice(0, 3).map((project) => (
                    <button
                      key={project.id}
                      className="dropdown-item"
                      onClick={() => handleResultClick('project', project)}
                    >
                      <FolderOpen className="h-4 w-4 text-primary" />
                      <div className="flex-1 text-left">
                        <div className="font-medium">{project.name}</div>
                        <div className="text-xs text-muted">{project.description || 'Aucune description'}</div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted" />
                    </button>
                  ))}
                </div>
              )}

              {/* Certifications RNCP */}
              {results.certifications.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-muted border-t">
                    CERTIFICATIONS RNCP ({results.certifications.length})
                  </div>
                  {results.certifications.slice(0, 3).map((cert) => (
                    <button
                      key={cert.id}
                      className="dropdown-item"
                      onClick={() => handleResultClick('certification', cert)}
                    >
                      <Award className="h-4 w-4 text-success" />
                      <div className="flex-1 text-left">
                        <div className="font-medium">{cert.rncpCode}</div>
                        <div className="text-xs text-muted">{cert.title || 'Sans titre'}</div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted" />
                    </button>
                  ))}
                </div>
              )}

              {/* Compétences */}
              {results.competences.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-muted border-t">
                    COMPÉTENCES ({results.competences.length})
                  </div>
                  {results.competences.slice(0, 3).map((comp) => (
                    <button
                      key={comp.id}
                      className="dropdown-item"
                      onClick={() => handleResultClick('competence', comp)}
                    >
                      <BookOpen className="h-4 w-4 text-accent" />
                      <div className="flex-1 text-left">
                        <div className="font-medium">{comp.code}</div>
                        <div className="text-xs text-muted">{comp.title || 'Sans titre'}</div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted" />
                    </button>
                  ))}
                </div>
              )}

              {/* Blocs de compétences */}
              {results.blocs.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-muted border-t">
                    BLOCS DE COMPÉTENCES ({results.blocs.length})
                  </div>
                  {results.blocs.slice(0, 3).map((bloc) => (
                    <button
                      key={bloc.id}
                      className="dropdown-item"
                      onClick={() => handleResultClick('bloc', bloc)}
                    >
                      <Package className="h-4 w-4 text-warning" />
                      <div className="flex-1 text-left">
                        <div className="font-medium">{bloc.code}</div>
                        <div className="text-xs text-muted">{bloc.title || 'Sans titre'}</div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted" />
                    </button>
                  ))}
                </div>
              )}

              {/* Métiers */}
              {results.metiers.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-muted border-t">
                    MÉTIERS ({results.metiers.length})
                  </div>
                  {results.metiers.slice(0, 3).map((metier) => (
                    <button
                      key={metier.id}
                      className="dropdown-item"
                      onClick={() => handleResultClick('metier', metier)}
                    >
                      <Briefcase className="h-4 w-4 text-primary" />
                      <div className="flex-1 text-left">
                        <div className="font-medium">{metier.libelleAppellation}</div>
                        <div className="text-xs text-muted">Code ROME: {metier.codeRome}</div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted" />
                    </button>
                  ))}
                </div>
              )}

              {/* Utilisateurs */}
              {results.users.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-muted border-t">
                    UTILISATEURS ({results.users.length})
                  </div>
                  {results.users.slice(0, 3).map((user) => (
                    <button
                      key={user.id}
                      className="dropdown-item"
                      onClick={() => handleResultClick('user', user)}
                    >
                      <Users className="h-4 w-4 text-info" />
                      <div className="flex-1 text-left">
                        <div className="font-medium">{user.firstName} {user.lastName}</div>
                        <div className="text-xs text-muted">{user.email}</div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted" />
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
